import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FilterListOff,
  PictureAsPdf as PictureAsPdfIcon,
  Refresh,
  UploadFile,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { DateInputField } from '../components/DateInputField';
import { TableFilterCell } from '../components/TableFilterCell';
import { BatchMaintenanceImportDialog } from '../components/maintenance/BatchMaintenanceImportDialog';
import { documentApi, vehicleMaintenanceApi } from '../services/api';
import {
  CompanyMaintenanceDashboard,
  CreateInsuranceRecordRequest,
  UpdateInsuranceRecordRequest,
  CreateInspectionRecordRequest,
  UpdateInspectionRecordRequest,
  CreateServiceCompletionRequest,
  UpdateServiceCompletionRequest,
  CreateServiceScheduleRequest,
  CreateTaxRecordRequest,
  UpdateTaxRecordRequest,
  CreateVehicleHistoryEventRequest,
  UpdateVehicleHistoryEventRequest,
  DueStatus,
  ServiceDueRuleType,
  ServiceSchedule,
  ServiceScheduleMode,
  ServiceScheduleStatus,
  ServiceTaskType,
  UpdateVehicleMileageRequest,
  VehicleAttachment,
  VehicleHistoryEventCategory,
} from '../types/vehicleMaintenance';
import { formatCurrency, formatDate, formatDateTime as formatAppDateTime } from '../utils/formatters';
import { safeFilePart } from '../utils/downloadFile';
import { statusLabel, enumLabel } from '../i18n/translate';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

type CreateRecordType = 'insurance' | 'inspection' | 'tax' | 'schedule' | 'completion' | 'event';
type CreateFlowStep = 'idle' | 'selectType' | 'form';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type ColumnFilters = {
  vehicle: string;
  plate: string;
  mileage: string;
  insurance: string;
  inspection: string;
  tax: string;
  service: string;
};

type MileageHistoryColumnFilters = {
  date: string;
  previousKm: string;
  newKm: string;
  difference: string;
  source: string;
  reference: string;
  updatedBy: string;
  notes: string;
};

type AttachmentOwnerType = 'INSURANCE' | 'INSPECTION' | 'TAX' | 'SERVICE_COMPLETION' | 'VEHICLE_EVENT';

type AttachmentOwnerOption = {
  ownerType: AttachmentOwnerType;
  label: string;
  records: Array<{
    id: string;
    label: string;
  }>;
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const serviceTypeOptions = Object.values(ServiceTaskType);
const eventCategoryOptions = Object.values(VehicleHistoryEventCategory);
const createRecordOptions: Array<{ value: CreateRecordType; label: string }> = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'inspection', label: 'Inspection / MOT' },
  { value: 'tax', label: 'Tax' },
  { value: 'schedule', label: 'Service schedule' },
  { value: 'completion', label: 'Record completed service' },
  { value: 'event', label: 'Vehicle history event' },
];

const createDefaultColumnFilters = (): ColumnFilters => ({
  vehicle: '',
  plate: '',
  mileage: '',
  insurance: '',
  inspection: '',
  tax: '',
  service: '',
});

const createDefaultMileageHistoryFilters = (): MileageHistoryColumnFilters => ({
  date: '',
  previousKm: '',
  newKm: '',
  difference: '',
  source: '',
  reference: '',
  updatedBy: '',
  notes: '',
});

const createDefaultInsuranceForm = () => ({
  policyNumber: '',
  providerCompany: '',
  startDate: todayInput(),
  expiryDate: '',
  cost: '',
  notes: '',
});

const createDefaultInspectionForm = () => ({
  inspectionDate: todayInput(),
  expiryDate: '',
  inspectionCenter: '',
  cost: '',
  notes: '',
});

const createDefaultTaxForm = () => ({
  taxType: '',
  paymentDate: todayInput(),
  expiryDate: '',
  amount: '',
  notes: '',
});

const createDefaultScheduleForm = () => ({
  serviceType: ServiceTaskType.OIL_CHANGE,
  serviceTaskLabel: '',
  description: '',
  scheduleMode: ServiceScheduleMode.RECURRING,
  dueRuleType: ServiceDueRuleType.MILEAGE_OR_CALENDAR,
  initialDueDate: '',
  initialDueMileageKm: '',
  recurrenceKilometersKm: '',
  recurrenceMonths: '',
  workshopProvider: '',
  notes: '',
});

const createDefaultCompletionForm = () => ({
  serviceScheduleId: '',
  serviceType: ServiceTaskType.OIL_CHANGE,
  serviceTaskLabel: '',
  completedOn: todayInput(),
  completedMileageKm: '',
  cost: '',
  workshopProvider: '',
  notes: '',
});

const createDefaultEventForm = () => ({
  category: VehicleHistoryEventCategory.CUSTOM,
  title: '',
  eventDate: todayInput(),
  mileageKm: '',
  cost: '',
  providerName: '',
  notes: '',
});

const createDefaultMileageUpdateForm = () => ({
  currentMileageKm: '',
  notes: '',
});

const parseVehicleLabel = (vehicleLabel: string) => {
  const [plate, ...rest] = vehicleLabel.split(' - ');
  if (!rest.length) {
    return { plate: '', name: vehicleLabel };
  }
  return { plate, name: rest.join(' - ') };
};

const toNullableNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toNullableInteger = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : undefined;
};

const statusColor = (status: DueStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'OK':
      return 'success';
    case 'DUE_SOON':
      return 'warning';
    case 'OVERDUE':
      return 'error';
    case 'COMPLETED':
      return 'info';
    default:
      return 'default';
  }
};

const statusRank = (status: DueStatus) => {
  switch (status) {
    case 'OVERDUE':
      return 0;
    case 'DUE_SOON':
      return 1;
    case 'MISSING':
      return 2;
    case 'OK':
      return 3;
    case 'COMPLETED':
      return 4;
    case 'INACTIVE':
      return 5;
    default:
      return 6;
  }
};

const friendlyStatus = (t: (key: string, fallback?: string) => string, status: DueStatus) =>
  enumLabel(t, 'dueStatuses', status);

const formatKm = (value?: number | null) => (
  value === null || value === undefined ? '-' : `${value.toLocaleString()} km`
);

const formatOptionalDate = (value?: string | null) => (
  value ? formatDate(value) : '-'
);

const formatOptionalCurrency = (value?: number | string | null) => (
  value === null || value === undefined ? '-' : formatCurrency(value)
);

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  return formatAppDateTime(value);
};

const formatMileageDifference = (value?: number | null) => {
  if (value === null || value === undefined) {
    return '-';
  }

  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value.toLocaleString()} km`;
};

const formatMileageSource = (t: (key: string, fallback?: string) => string, value?: string | null) => (
  value ? enumLabel(t, 'mileageSources', value) : '-'
);

const countDueVehicles = (dashboard?: CompanyMaintenanceDashboard) => (
  dashboard?.vehicles.filter((item) =>
    item.insurance.status === 'DUE_SOON' ||
    item.insurance.status === 'OVERDUE' ||
    item.inspection.status === 'DUE_SOON' ||
    item.inspection.status === 'OVERDUE' ||
    item.tax.status === 'DUE_SOON' ||
    item.tax.status === 'OVERDUE' ||
    item.dueServiceSchedules.length > 0
  ).length ?? 0
);

const historyTypeLabel = (t: (key: string, fallback?: string) => string, entry: { sourceType: string; category: string }) => {
  switch (entry.sourceType) {
    case 'INSURANCE':
      return t('maintenance.historyType.insurance', 'Insurance');
    case 'INSPECTION':
      return t('maintenance.historyType.inspection', 'Inspection / MOT');
    case 'TAX':
      return t('maintenance.historyType.tax', 'Tax');
    case 'SERVICE_COMPLETION':
      return t('maintenance.historyType.service', 'Service');
    case 'HISTORY_EVENT':
      return enumLabel(t, 'vehicleEventCategories', entry.category);
    default:
      return enumLabel(t, 'mileageSources', entry.sourceType);
  }
};

const editableRecordTypeForSourceType = (sourceType: string): CreateRecordType | null => {
  switch (sourceType) {
    case 'INSURANCE':
      return 'insurance';
    case 'INSPECTION':
      return 'inspection';
    case 'TAX':
      return 'tax';
    case 'SERVICE_COMPLETION':
      return 'completion';
    case 'HISTORY_EVENT':
      return 'event';
    default:
      return null;
  }
};

const formatScheduleDueTarget = (schedule: ServiceSchedule) => {
  const parts: string[] = [];
  if (schedule.nextDueMileageKm !== null && schedule.nextDueMileageKm !== undefined) {
    parts.push(`${schedule.nextDueMileageKm.toLocaleString()} km`);
  }
  if (schedule.nextDueDate) {
    parts.push(formatDate(schedule.nextDueDate));
  }
  return parts.length ? parts.join(' / ') : '—';
};

const formatScheduleLastCompletion = (schedule: ServiceSchedule) => {
  const parts: string[] = [];
  if (schedule.lastCompletedMileageKm !== null && schedule.lastCompletedMileageKm !== undefined) {
    parts.push(`${schedule.lastCompletedMileageKm.toLocaleString()} km`);
  }
  if (schedule.lastCompletedOn) {
    parts.push(formatDate(schedule.lastCompletedOn));
  }
  return parts.length ? parts.join(' / ') : '—';
};

const formatAttachmentFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
};

const formatAttachmentDateTime = (t: (key: string, fallback?: string) => string, value?: string | null) => {
  if (!value) {
    return t('maintenance.attachments.dateUnavailable', 'Unavailable');
  }

  return formatDateTime(value);
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-tabpanel-${index}`}
      aria-labelledby={`maintenance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

const SummaryTile: React.FC<{
  title: string;
  value: string;
  helper?: string;
  status?: DueStatus;
}> = ({ title, value, helper, status = 'OK' }) => {
  const { t } = useTranslation();
  return (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      height: '100%',
      borderColor: status === 'OVERDUE' ? 'error.light' : 'divider',
      bgcolor: status === 'OVERDUE' ? 'error.50' : 'background.paper',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Chip size="small" label={friendlyStatus(t, status)} color={statusColor(status)} />
    </Stack>
    <Typography variant="body1" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
    {helper && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {helper}
      </Typography>
    )}
  </Paper>
  );
};

export const MaintenancePage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [openAttachmentsDialog, setOpenAttachmentsDialog] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedAttachmentOwnerType, setSelectedAttachmentOwnerType] = useState<AttachmentOwnerType | ''>('');
  const [selectedAttachmentOwnerRecordId, setSelectedAttachmentOwnerRecordId] = useState('');
  const [selectedAttachmentFile, setSelectedAttachmentFile] = useState<File | null>(null);
  const [attachmentNotes, setAttachmentNotes] = useState('');
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(0);
  const [detailTab, setDetailTab] = useState(0);
  const [createFlowStep, setCreateFlowStep] = useState<CreateFlowStep>('idle');
  const [createRecordType, setCreateRecordType] = useState<CreateRecordType | ''>('');
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(createDefaultColumnFilters());
  const [sortConfig, setSortConfig] = useState<{ key: keyof ColumnFilters; direction: 'asc' | 'desc' } | null>(null);
  const [mileageHistoryFilters, setMileageHistoryFilters] = useState<MileageHistoryColumnFilters>(createDefaultMileageHistoryFilters());
  const [mileageHistorySortConfig, setMileageHistorySortConfig] = useState<{ key: keyof MileageHistoryColumnFilters; direction: 'asc' | 'desc' }>({
    key: 'date',
    direction: 'desc',
  });
  const [isMileageEditorOpen, setIsMileageEditorOpen] = useState(false);

  const [insuranceForm, setInsuranceForm] = useState(createDefaultInsuranceForm);
  const [inspectionForm, setInspectionForm] = useState(createDefaultInspectionForm);
  const [taxForm, setTaxForm] = useState(createDefaultTaxForm);
  const [maintenanceExtractFile, setMaintenanceExtractFile] = useState<File | null>(null);
  const [maintenanceAiFilledFields, setMaintenanceAiFilledFields] = useState<Set<string>>(new Set());
  const [batchImportOpen, setBatchImportOpen] = useState(false);
  const aiFilledFieldSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' } };
  const [scheduleForm, setScheduleForm] = useState(createDefaultScheduleForm);
  const [completionForm, setCompletionForm] = useState(createDefaultCompletionForm);
  const [eventForm, setEventForm] = useState(createDefaultEventForm);
  const [mileageUpdateForm, setMileageUpdateForm] = useState(createDefaultMileageUpdateForm);

  const dashboardQuery = useQuery('vehicleMaintenanceDashboard', vehicleMaintenanceApi.getDashboard);

  useEffect(() => {
    const vehicles = dashboardQuery.data?.vehicles ?? [];
    if (!vehicles.length) {
      if (selectedVehicleId) {
        setSelectedVehicleId('');
      }
      return;
    }

    if (!selectedVehicleId || !vehicles.some((item) => String(item.vehicleId) === selectedVehicleId)) {
      setSelectedVehicleId(String(vehicles[0].vehicleId));
    }
  }, [dashboardQuery.data, selectedVehicleId]);

  const overviewQuery = useQuery(
    ['vehicleMaintenanceOverview', selectedVehicleId],
    () => vehicleMaintenanceApi.getOverview(selectedVehicleId),
    { enabled: !!selectedVehicleId }
  );

  const refreshMaintenanceQueries = () => {
    queryClient.invalidateQueries('vehicleMaintenanceDashboard');
    if (selectedVehicleId) {
      queryClient.invalidateQueries(['vehicleMaintenanceOverview', selectedVehicleId]);
    }
  };

  const resetAllCreateForms = () => {
    setInsuranceForm(createDefaultInsuranceForm());
    setInspectionForm(createDefaultInspectionForm());
    setTaxForm(createDefaultTaxForm());
    setScheduleForm(createDefaultScheduleForm());
    setCompletionForm(createDefaultCompletionForm());
    setEventForm(createDefaultEventForm());
    setMaintenanceExtractFile(null);
    setMaintenanceAiFilledFields(new Set());
  };

  const exitCreateFlow = () => {
    setCreateFlowStep('idle');
    setCreateRecordType('');
    setEditingRecordId(null);
  };

  const cancelCreateFlow = () => {
    resetAllCreateForms();
    exitCreateFlow();
  };

  const startCreateFlow = () => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    resetAllCreateForms();
    setCreateRecordType('');
    setEditingRecordId(null);
    setCreateFlowStep('selectType');
  };

  const startEditFlow = (type: CreateRecordType, recordId: number) => {
    const overview = overviewQuery.data;
    if (!overview) {
      return;
    }

    switch (type) {
      case 'insurance': {
        const record = overview.insuranceRecords.find((item) => item.id === recordId);
        if (!record) return;
        setInsuranceForm({
          policyNumber: record.policyNumber,
          providerCompany: record.providerCompany,
          startDate: record.startDate,
          expiryDate: record.expiryDate,
          cost: record.cost != null ? String(record.cost) : '',
          notes: record.notes ?? '',
        });
        break;
      }
      case 'inspection': {
        const record = overview.inspectionRecords.find((item) => item.id === recordId);
        if (!record) return;
        setInspectionForm({
          inspectionDate: record.inspectionDate,
          expiryDate: record.expiryDate,
          inspectionCenter: record.inspectionCenter,
          cost: record.cost != null ? String(record.cost) : '',
          notes: record.notes ?? '',
        });
        break;
      }
      case 'tax': {
        const record = overview.taxRecords.find((item) => item.id === recordId);
        if (!record) return;
        setTaxForm({
          taxType: record.taxType,
          paymentDate: record.paymentDate,
          expiryDate: record.expiryDate,
          amount: record.amount != null ? String(record.amount) : '',
          notes: record.notes ?? '',
        });
        break;
      }
      case 'completion': {
        const record = overview.serviceHistory.find((item) => item.id === recordId);
        if (!record) return;
        setCompletionForm({
          serviceScheduleId: record.serviceScheduleId != null ? String(record.serviceScheduleId) : '',
          serviceType: (record.serviceType as ServiceTaskType) ?? ServiceTaskType.OIL_CHANGE,
          serviceTaskLabel: record.serviceTaskLabel,
          completedOn: record.completedOn,
          completedMileageKm: record.completedMileageKm != null ? String(record.completedMileageKm) : '',
          cost: record.cost != null ? String(record.cost) : '',
          workshopProvider: record.workshopProvider ?? '',
          notes: record.notes ?? '',
        });
        break;
      }
      case 'event': {
        const record = overview.customEvents.find((item) => item.id === recordId);
        if (!record) return;
        setEventForm({
          category: (record.category as VehicleHistoryEventCategory) ?? VehicleHistoryEventCategory.CUSTOM,
          title: record.title,
          eventDate: record.eventDate,
          mileageKm: record.mileageKm != null ? String(record.mileageKm) : '',
          cost: record.cost != null ? String(record.cost) : '',
          providerName: record.providerName ?? '',
          notes: record.notes ?? '',
        });
        break;
      }
      default:
        return;
    }

    setCreateRecordType(type);
    setEditingRecordId(recordId);
    setCreateFlowStep('form');
  };

  const extractMaintenanceDocMutation = useMutation(documentApi.uploadAndExtract, {
    onSuccess: (response) => {
      const fields = response.extraction?.fields || [];
      const valuesByField = new Map(fields.map((field) => [field.fieldName, field.normalizedValue || field.fieldValue]));
      const filledFields = new Set<string>();

      const applyText = (currentValue: string, fieldName: string): string => {
        const value = valuesByField.get(fieldName);
        if (value) {
          filledFields.add(fieldName);
          return value;
        }
        return currentValue;
      };

      if (createRecordType === 'insurance') {
        setInsuranceForm((current) => ({
          ...current,
          policyNumber: applyText(current.policyNumber, 'policyNumber'),
          providerCompany: applyText(current.providerCompany, 'providerCompany'),
          startDate: applyText(current.startDate, 'startDate'),
          expiryDate: applyText(current.expiryDate, 'expiryDate'),
          cost: applyText(current.cost, 'cost'),
        }));
      } else if (createRecordType === 'inspection') {
        setInspectionForm((current) => ({
          ...current,
          inspectionCenter: applyText(current.inspectionCenter, 'inspectionCenter'),
          inspectionDate: applyText(current.inspectionDate, 'inspectionDate'),
          expiryDate: applyText(current.expiryDate, 'expiryDate'),
          cost: applyText(current.cost, 'cost'),
        }));
      } else if (createRecordType === 'tax') {
        setTaxForm((current) => ({
          ...current,
          taxType: applyText(current.taxType, 'taxType'),
          paymentDate: applyText(current.paymentDate, 'paymentDate'),
          expiryDate: applyText(current.expiryDate, 'expiryDate'),
          amount: applyText(current.amount, 'amount'),
        }));
      }

      setMaintenanceAiFilledFields(filledFields);

      if (response.processingStatus === 'COMPLETED') {
        toast.success(t('maintenance.extract.success', 'Document details extracted. Please review before saving.'));
      } else if (response.processingStatus === 'SKIPPED') {
        toast(response.extraction?.failureReason || t('maintenance.extract.skipped', 'AI extraction is not enabled for this company.'));
      } else {
        toast.error(response.extraction?.failureReason || t('maintenance.extract.failed', 'Could not read the document.'));
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || error.message || t('maintenance.extract.failed', 'Could not read the document.'));
    },
  });

  const insuranceMutation = useMutation(
    (data: CreateInsuranceRecordRequest) => vehicleMaintenanceApi.createInsuranceRecord(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.insuranceAdded', 'Insurance record added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.insuranceAddFailed', 'Failed to add insurance record'));
      },
    }
  );

  const updateInsuranceMutation = useMutation(
    (data: UpdateInsuranceRecordRequest) => vehicleMaintenanceApi.updateInsuranceRecord(selectedVehicleId, String(editingRecordId), data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.insuranceUpdated', 'Insurance record updated'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.insuranceUpdateFailed', 'Failed to update insurance record'));
      },
    }
  );

  const inspectionMutation = useMutation(
    (data: CreateInspectionRecordRequest) => vehicleMaintenanceApi.createInspectionRecord(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.inspectionAdded', 'Inspection record added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.inspectionAddFailed', 'Failed to add inspection record'));
      },
    }
  );

  const updateInspectionMutation = useMutation(
    (data: UpdateInspectionRecordRequest) => vehicleMaintenanceApi.updateInspectionRecord(selectedVehicleId, String(editingRecordId), data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.inspectionUpdated', 'Inspection record updated'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.inspectionUpdateFailed', 'Failed to update inspection record'));
      },
    }
  );

  const taxMutation = useMutation(
    (data: CreateTaxRecordRequest) => vehicleMaintenanceApi.createTaxRecord(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.taxAdded', 'Tax record added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.taxAddFailed', 'Failed to add tax record'));
      },
    }
  );

  const updateTaxMutation = useMutation(
    (data: UpdateTaxRecordRequest) => vehicleMaintenanceApi.updateTaxRecord(selectedVehicleId, String(editingRecordId), data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.taxUpdated', 'Tax record updated'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.taxUpdateFailed', 'Failed to update tax record'));
      },
    }
  );

  const scheduleMutation = useMutation(
    (data: CreateServiceScheduleRequest) => vehicleMaintenanceApi.createServiceSchedule(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.scheduleAdded', 'Service schedule added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.scheduleAddFailed', 'Failed to add service schedule'));
      },
    }
  );

  const completionMutation = useMutation(
    (data: CreateServiceCompletionRequest) => vehicleMaintenanceApi.createServiceCompletion(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.completionAdded', 'Service completion added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.completionAddFailed', 'Failed to add service completion'));
      },
    }
  );

  const updateCompletionMutation = useMutation(
    (data: UpdateServiceCompletionRequest) => vehicleMaintenanceApi.updateServiceCompletion(selectedVehicleId, String(editingRecordId), data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.completionUpdated', 'Service completion updated'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.completionUpdateFailed', 'Failed to update service completion'));
      },
    }
  );

  const historyMutation = useMutation(
    (data: CreateVehicleHistoryEventRequest) => vehicleMaintenanceApi.createHistoryEvent(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.eventAdded', 'Vehicle event added'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.eventAddFailed', 'Failed to add vehicle event'));
      },
    }
  );

  const updateHistoryMutation = useMutation(
    (data: UpdateVehicleHistoryEventRequest) => vehicleMaintenanceApi.updateHistoryEvent(selectedVehicleId, String(editingRecordId), data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.eventUpdated', 'Vehicle event updated'));
        refreshMaintenanceQueries();
        cancelCreateFlow();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.eventUpdateFailed', 'Failed to update vehicle event'));
      },
    }
  );

  const mileageUpdateMutation = useMutation(
    (data: UpdateVehicleMileageRequest) => vehicleMaintenanceApi.updateMileage(selectedVehicleId, data),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.mileageUpdated', 'Vehicle kilometers updated'));
        refreshMaintenanceQueries();
        setIsMileageEditorOpen(false);
        setMileageUpdateForm(createDefaultMileageUpdateForm());
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.mileageUpdateFailed', 'Failed to update vehicle kilometers'));
      },
    }
  );

  const scheduleStatusMutation = useMutation(
    ({ scheduleId, status }: { scheduleId: number; status: ServiceScheduleStatus }) =>
      vehicleMaintenanceApi.updateServiceScheduleStatus(String(scheduleId), { status }),
    {
      onSuccess: () => {
        toast.success(t('maintenance.toasts.scheduleStatusUpdated', 'Schedule updated'));
        refreshMaintenanceQueries();
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.scheduleStatusUpdateFailed', 'Failed to update schedule'));
      },
    }
  );

  const attachmentUploadMutation = useMutation(
    () => {
      if (!selectedVehicleId) {
        return Promise.reject(new Error(t('maintenance.errors.noVehicleSelected', 'No vehicle selected')));
      }
      if (!selectedAttachmentFile) {
        return Promise.reject(new Error(t('maintenance.errors.noAttachmentSelected', 'No attachment selected')));
      }

      return vehicleMaintenanceApi.uploadAttachment({
        vehicleId: selectedVehicleId,
        ownerType: selectedAttachmentOwnerType,
        ownerRecordId: selectedAttachmentOwnerRecordId,
        notes: attachmentNotes,
        file: selectedAttachmentFile,
        onUploadProgress: setAttachmentUploadProgress,
      });
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['vehicleMaintenanceOverview', selectedVehicleId]);
        setSelectedAttachmentFile(null);
        setAttachmentNotes('');
        setAttachmentUploadProgress(0);
        toast.success(t('maintenance.toasts.attachmentUploaded', 'Attachment uploaded successfully'));
      },
      onError: (error: any) => {
        setAttachmentUploadProgress(0);
        toast.error(error.response?.data || error.message || t('maintenance.toasts.attachmentUploadFailed', 'Failed to upload attachment'));
      },
    }
  );

  const attachmentDeleteMutation = useMutation(
    (attachmentId: string) => vehicleMaintenanceApi.deleteAttachment(selectedVehicleId, attachmentId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['vehicleMaintenanceOverview', selectedVehicleId]);
        toast.success(t('maintenance.toasts.attachmentDeleted', 'Attachment deleted successfully'));
      },
      onError: (error: any) => {
        toast.error(error.response?.data || error.message || t('maintenance.toasts.attachmentDeleteFailed', 'Failed to delete attachment'));
      },
    }
  );

  const selectedDueItem = useMemo(
    () => dashboardQuery.data?.vehicles.find((item) => String(item.vehicleId) === selectedVehicleId),
    [dashboardQuery.data, selectedVehicleId]
  );

  const selectedSchedule = useMemo(() => {
    if (!completionForm.serviceScheduleId || !overviewQuery.data) return null;
    return overviewQuery.data.serviceSchedules.find(
      (schedule) => String(schedule.id) === completionForm.serviceScheduleId
    ) || null;
  }, [completionForm.serviceScheduleId, overviewQuery.data]);

  useEffect(() => {
    if (selectedSchedule) {
      setCompletionForm((current) => ({
        ...current,
        serviceType: (selectedSchedule.serviceType as ServiceTaskType) || current.serviceType,
        serviceTaskLabel: selectedSchedule.serviceTaskLabel || current.serviceTaskLabel,
        workshopProvider: selectedSchedule.workshopProvider || current.workshopProvider,
      }));
    }
  }, [selectedSchedule]);

  const attachmentOwnerOptions = useMemo<AttachmentOwnerOption[]>(() => {
    if (!overviewQuery.data) {
      return [];
    }

    const options: AttachmentOwnerOption[] = [];

    if (overviewQuery.data.insuranceRecords.length > 0) {
      options.push({
        ownerType: 'INSURANCE',
        label: t('maintenance.attachmentOwner.insurance', 'Insurance'),
        records: overviewQuery.data.insuranceRecords.map((record) => ({
          id: String(record.id),
          label: `${record.policyNumber} - ${record.providerCompany} - ${formatDate(record.expiryDate)}`,
        })),
      });
    }

    if (overviewQuery.data.inspectionRecords.length > 0) {
      options.push({
        ownerType: 'INSPECTION',
        label: t('maintenance.attachmentOwner.inspection', 'Inspection / MOT'),
        records: overviewQuery.data.inspectionRecords.map((record) => ({
          id: String(record.id),
          label: `${formatDate(record.inspectionDate)} - ${record.inspectionCenter} - ${formatDate(record.expiryDate)}`,
        })),
      });
    }

    if (overviewQuery.data.taxRecords.length > 0) {
      options.push({
        ownerType: 'TAX',
        label: t('maintenance.attachmentOwner.tax', 'Tax'),
        records: overviewQuery.data.taxRecords.map((record) => ({
          id: String(record.id),
          label: `${record.taxType} - ${formatDate(record.paymentDate)} - ${formatDate(record.expiryDate)}`,
        })),
      });
    }

    if (overviewQuery.data.serviceHistory.length > 0) {
      options.push({
        ownerType: 'SERVICE_COMPLETION',
        label: t('maintenance.attachmentOwner.serviceHistory', 'Service history'),
        records: overviewQuery.data.serviceHistory.map((record) => ({
          id: String(record.id),
          label: `${record.serviceTaskLabel} - ${formatDate(record.completedOn)}`,
        })),
      });
    }

    if (overviewQuery.data.customEvents.length > 0) {
      options.push({
        ownerType: 'VEHICLE_EVENT',
        label: t('maintenance.attachmentOwner.vehicleEvent', 'Vehicle event'),
        records: overviewQuery.data.customEvents.map((record) => ({
          id: String(record.id),
          label: `${record.title} - ${formatDate(record.eventDate)}`,
        })),
      });
    }

    return options;
  }, [overviewQuery.data, t]);

  const selectedAttachmentOwnerOption = useMemo(
    () => attachmentOwnerOptions.find((option) => option.ownerType === selectedAttachmentOwnerType) ?? null,
    [attachmentOwnerOptions, selectedAttachmentOwnerType]
  );

  const attachmentOwnerLabelByKey = useMemo(() => {
    const labels = new Map<string, string>();

    attachmentOwnerOptions.forEach((option) => {
      option.records.forEach((record) => {
        labels.set(`${option.ownerType}:${record.id}`, `${option.label} - ${record.label}`);
      });
    });

    return labels;
  }, [attachmentOwnerOptions]);

  const filteredVehicles = useMemo(() => {
    const items = [...(dashboardQuery.data?.vehicles ?? [])];

    const matchesFilters = items.filter((item) => {
      const parsed = parseVehicleLabel(item.vehicleLabel);

      return (
        (!columnFilters.vehicle || parsed.name.toLowerCase().includes(columnFilters.vehicle.toLowerCase())) &&
        (!columnFilters.plate || parsed.plate.toLowerCase().includes(columnFilters.plate.toLowerCase())) &&
        (!columnFilters.mileage || String(item.currentMileageKm ?? '').includes(columnFilters.mileage)) &&
        (!columnFilters.insurance || friendlyStatus(t, item.insurance.status).toLowerCase().includes(columnFilters.insurance.toLowerCase())) &&
        (!columnFilters.inspection || friendlyStatus(t, item.inspection.status).toLowerCase().includes(columnFilters.inspection.toLowerCase())) &&
        (!columnFilters.tax || friendlyStatus(t, item.tax.status).toLowerCase().includes(columnFilters.tax.toLowerCase())) &&
        (!columnFilters.service || String(item.dueServiceSchedules.length).includes(columnFilters.service))
      );
    });

    if (!sortConfig) {
      return matchesFilters;
    }

    return matchesFilters.sort((left, right) => {
      const leftParsed = parseVehicleLabel(left.vehicleLabel);
      const rightParsed = parseVehicleLabel(right.vehicleLabel);

      let comparison = 0;
      switch (sortConfig.key) {
        case 'vehicle':
          comparison = leftParsed.name.localeCompare(rightParsed.name);
          break;
        case 'plate':
          comparison = leftParsed.plate.localeCompare(rightParsed.plate);
          break;
        case 'mileage':
          comparison = (left.currentMileageKm ?? -1) - (right.currentMileageKm ?? -1);
          break;
        case 'insurance':
          comparison = statusRank(left.insurance.status) - statusRank(right.insurance.status);
          break;
        case 'inspection':
          comparison = statusRank(left.inspection.status) - statusRank(right.inspection.status);
          break;
        case 'tax':
          comparison = statusRank(left.tax.status) - statusRank(right.tax.status);
          break;
        case 'service':
          comparison = left.dueServiceSchedules.length - right.dueServiceSchedules.length;
          break;
        default:
          comparison = 0;
      }

      return sortConfig.direction === 'asc' ? comparison : comparison * -1;
    });
  }, [columnFilters, dashboardQuery.data, sortConfig, t]);

  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / rowsPerPage));
  const safePage = Math.min(page, totalPages - 1);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const paginatedVehicles = useMemo(
    () => filteredVehicles.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [filteredVehicles, rowsPerPage, safePage]
  );

  const mileageHistoryRows = useMemo(() => {
    const entries = [...(overviewQuery.data?.mileageHistory ?? [])];
    const filteredEntries = entries.filter((entry) => (
      (!mileageHistoryFilters.date || formatDateTime(entry.updatedAt).toLowerCase().includes(mileageHistoryFilters.date.toLowerCase())) &&
      (!mileageHistoryFilters.previousKm || String(entry.previousMileageKm ?? '').includes(mileageHistoryFilters.previousKm)) &&
      (!mileageHistoryFilters.newKm || String(entry.newMileageKm ?? '').includes(mileageHistoryFilters.newKm)) &&
      (!mileageHistoryFilters.difference || String(entry.differenceKm ?? '').includes(mileageHistoryFilters.difference)) &&
      (!mileageHistoryFilters.source || formatMileageSource(t, entry.source).toLowerCase().includes(mileageHistoryFilters.source.toLowerCase())) &&
      (!mileageHistoryFilters.reference || (entry.reference ?? '').toLowerCase().includes(mileageHistoryFilters.reference.toLowerCase())) &&
      (!mileageHistoryFilters.updatedBy || (entry.updatedByDisplayName ?? '').toLowerCase().includes(mileageHistoryFilters.updatedBy.toLowerCase())) &&
      (!mileageHistoryFilters.notes || (entry.notes ?? '').toLowerCase().includes(mileageHistoryFilters.notes.toLowerCase()))
    ));

    return filteredEntries.sort((left, right) => {
      let comparison = 0;

      switch (mileageHistorySortConfig.key) {
        case 'date':
          comparison = new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
          break;
        case 'previousKm':
          comparison = (left.previousMileageKm ?? -1) - (right.previousMileageKm ?? -1);
          break;
        case 'newKm':
          comparison = (left.newMileageKm ?? -1) - (right.newMileageKm ?? -1);
          break;
        case 'difference':
          comparison = (left.differenceKm ?? -1) - (right.differenceKm ?? -1);
          break;
        case 'source':
          comparison = formatMileageSource(t, left.source).localeCompare(formatMileageSource(t, right.source));
          break;
        case 'reference':
          comparison = (left.reference ?? '').localeCompare(right.reference ?? '');
          break;
        case 'updatedBy':
          comparison = (left.updatedByDisplayName ?? '').localeCompare(right.updatedByDisplayName ?? '');
          break;
        case 'notes':
          comparison = (left.notes ?? '').localeCompare(right.notes ?? '');
          break;
        default:
          comparison = 0;
      }

      if (comparison === 0) {
        comparison = new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
      }

      return mileageHistorySortConfig.direction === 'asc' ? comparison : comparison * -1;
    });
  }, [mileageHistoryFilters, mileageHistorySortConfig, overviewQuery.data, t]);

  const serviceHistoryRows = useMemo(() => {
    const entries = [...(overviewQuery.data?.serviceHistory ?? [])];
    return entries.sort((left, right) => {
      const leftCompleted = left.completedOn ? new Date(left.completedOn).getTime() : 0;
      const rightCompleted = right.completedOn ? new Date(right.completedOn).getTime() : 0;

      if (leftCompleted !== rightCompleted) {
        return rightCompleted - leftCompleted;
      }

      const leftCreated = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightCreated = right.createdAt ? new Date(right.createdAt).getTime() : 0;
      return rightCreated - leftCreated;
    });
  }, [overviewQuery.data]);

  const historyRows = useMemo(() => {
    const entries = [...(overviewQuery.data?.timeline ?? [])];
    return entries.sort((left, right) => {
      const leftMileage = left.mileageKm;
      const rightMileage = right.mileageKm;

      if (leftMileage !== null && leftMileage !== undefined && rightMileage !== null && rightMileage !== undefined) {
        if (leftMileage !== rightMileage) {
          return rightMileage - leftMileage;
        }
      } else if (leftMileage !== null && leftMileage !== undefined) {
        return -1;
      } else if (rightMileage !== null && rightMileage !== undefined) {
        return 1;
      }

      const leftDate = left.occurredOn ? new Date(left.occurredOn).getTime() : 0;
      const rightDate = right.occurredOn ? new Date(right.occurredOn).getTime() : 0;
      return rightDate - leftDate;
    });
  }, [overviewQuery.data]);

  const scheduleDialogNeedsDate = scheduleForm.dueRuleType !== ServiceDueRuleType.MILEAGE;
  const scheduleDialogNeedsMileage = scheduleForm.dueRuleType !== ServiceDueRuleType.CALENDAR;
  const scheduleDialogIsRecurring = scheduleForm.scheduleMode === ServiceScheduleMode.RECURRING;

  const selectedVehicleName = useMemo(() => {
    if (overviewQuery.data?.vehicle) {
      const { vehicle } = overviewQuery.data;
      const vehicleName = `${vehicle.make} ${vehicle.model}`.trim();
      return vehicle.licensePlate ? `${vehicle.licensePlate} - ${vehicleName}` : vehicleName;
    }
    return selectedDueItem?.vehicleLabel || t('maintenance.fallback.vehicleMaintenance', 'Vehicle maintenance');
  }, [overviewQuery.data, selectedDueItem, t]);

  const exportHistoryMutation = useMutation(
    () => {
      if (!selectedVehicleId) {
        return Promise.reject(new Error(t('maintenance.errors.noVehicleSelected', 'No vehicle selected')));
      }
      return vehicleMaintenanceApi.downloadHistoryPdf(selectedVehicleId);
    },
    {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `maintenance-history-${safeFilePart(selectedVehicleName, selectedVehicleId || 'vehicle')}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        toast.success(t('maintenance.toasts.historyPdfDownloaded', 'Maintenance history PDF downloaded'));
      },
      onError: () => {
        toast.error(t('maintenance.toasts.historyExportFailed', 'Failed to export maintenance history'));
      },
    }
  );

  useEffect(() => {
    if (!attachmentOwnerOptions.length) {
      if (selectedAttachmentOwnerType) {
        setSelectedAttachmentOwnerType('');
      }
      if (selectedAttachmentOwnerRecordId) {
        setSelectedAttachmentOwnerRecordId('');
      }
      return;
    }

    if (!selectedAttachmentOwnerType || !attachmentOwnerOptions.some((option) => option.ownerType === selectedAttachmentOwnerType)) {
      setSelectedAttachmentOwnerType(attachmentOwnerOptions[0].ownerType);
      return;
    }

    const currentOption = attachmentOwnerOptions.find((option) => option.ownerType === selectedAttachmentOwnerType);
    if (!currentOption) {
      return;
    }

    if (!selectedAttachmentOwnerRecordId || !currentOption.records.some((record) => record.id === selectedAttachmentOwnerRecordId)) {
      setSelectedAttachmentOwnerRecordId(currentOption.records[0]?.id || '');
    }
  }, [attachmentOwnerOptions, selectedAttachmentOwnerRecordId, selectedAttachmentOwnerType]);

  useEffect(() => {
    if (!selectedVehicleId && createFlowStep !== 'idle') {
      setInsuranceForm(createDefaultInsuranceForm());
      setInspectionForm(createDefaultInspectionForm());
      setTaxForm(createDefaultTaxForm());
      setScheduleForm(createDefaultScheduleForm());
      setCompletionForm(createDefaultCompletionForm());
      setEventForm(createDefaultEventForm());
      setCreateFlowStep('idle');
      setCreateRecordType('');
    }
  }, [createFlowStep, selectedVehicleId]);

  useEffect(() => {
    setDetailTab(0);
    setIsMileageEditorOpen(false);
    setMileageUpdateForm(createDefaultMileageUpdateForm());
    setMileageHistoryFilters(createDefaultMileageHistoryFilters());
    setMileageHistorySortConfig({
      key: 'date',
      direction: 'desc',
    });
  }, [selectedVehicleId]);

  const activeSchedules = overviewQuery.data?.serviceSchedules ?? [];
  const activeScheduleCount = activeSchedules.filter((schedule) => schedule.status === ServiceScheduleStatus.ACTIVE).length;
  const overdueScheduleCount = activeSchedules.filter((schedule) => schedule.dueStatus === 'OVERDUE').length;

  const clearFilters = () => {
    setColumnFilters(createDefaultColumnFilters());
    setSortConfig(null);
    setPage(0);
  };

  const clearMileageHistoryFilters = () => {
    setMileageHistoryFilters(createDefaultMileageHistoryFilters());
    setMileageHistorySortConfig({
      key: 'date',
      direction: 'desc',
    });
  };

  const handleSort = (key: keyof ColumnFilters) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return current.direction === 'asc'
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  const handleMileageHistorySort = (key: keyof MileageHistoryColumnFilters) => {
    setMileageHistorySortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return { key, direction: key === 'date' ? 'desc' : 'asc' };
    });
  };

  const handleOpenMileageEditor = () => {
    const currentMileage = overviewQuery.data?.mileage.currentMileageKm
      ?? overviewQuery.data?.vehicle.currentMileageKm
      ?? selectedDueItem?.currentMileageKm;

    setMileageUpdateForm({
      currentMileageKm: currentMileage !== null && currentMileage !== undefined ? String(currentMileage) : '',
      notes: '',
    });
    setIsMileageEditorOpen(true);
  };

  const handleCancelMileageEditor = () => {
    if (mileageUpdateMutation.isLoading) {
      return;
    }

    setMileageUpdateForm(createDefaultMileageUpdateForm());
    setIsMileageEditorOpen(false);
  };

  const handleSaveMileageUpdate = () => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    const currentMileageKm = toNullableInteger(mileageUpdateForm.currentMileageKm);
    if (currentMileageKm === undefined || currentMileageKm < 0) {
      toast.error(t('maintenance.errors.invalidMileage', 'Enter a valid current kilometers value'));
      return;
    }

    mileageUpdateMutation.mutate({
      currentMileageKm,
      notes: mileageUpdateForm.notes.trim() || undefined,
    });
  };

  const handleOpenAttachmentsDialog = () => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    setOpenAttachmentsDialog(true);
  };

  const handleCloseAttachmentsDialog = () => {
    if (attachmentUploadMutation.isLoading) {
      return;
    }

    setOpenAttachmentsDialog(false);
    setSelectedAttachmentFile(null);
    setAttachmentNotes('');
    setAttachmentUploadProgress(0);
  };

  const handleUploadAttachment = () => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }
    if (!selectedAttachmentOwnerType) {
      toast.error(t('maintenance.errors.selectRecordTypeFirst', 'Select a record type first'));
      return;
    }
    if (!selectedAttachmentOwnerRecordId) {
      toast.error(t('maintenance.errors.selectMaintenanceRecordFirst', 'Select a maintenance record first'));
      return;
    }
    if (!selectedAttachmentFile) {
      toast.error(t('maintenance.errors.chooseAttachmentFirst', 'Choose an attachment first'));
      return;
    }

    attachmentUploadMutation.mutate();
  };

  const handlePreviewAttachment = async (attachment: VehicleAttachment) => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    try {
      const blob = await vehicleMaintenanceApi.previewAttachment(selectedVehicleId, String(attachment.id));
      const url = URL.createObjectURL(blob);
      const previewWindow = window.open(url, '_blank', 'noopener,noreferrer');

      if (!previewWindow) {
        const link = window.document.createElement('a');
        link.href = url;
        link.download = safeFilePart(attachment.originalFileName, `maintenance-attachment-${attachment.id}`);
        window.document.body.appendChild(link);
        link.click();
        link.remove();
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.message ||
          t('maintenance.toasts.attachmentPreviewFailed', 'Failed to preview attachment')
      );
    }
  };

  const handleDownloadAttachment = async (attachment: VehicleAttachment) => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    try {
      const blob = await vehicleMaintenanceApi.downloadAttachment(selectedVehicleId, String(attachment.id));
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = safeFilePart(attachment.originalFileName, `maintenance-attachment-${attachment.id}`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.message ||
          t('maintenance.toasts.attachmentDownloadFailed', 'Failed to download attachment')
      );
    }
  };

  const handleDeleteAttachment = (attachment: VehicleAttachment) => {
    const confirmed = window.confirm(
      t('maintenance.toasts.attachmentDeleteConfirm', 'Delete "{{fileName}}"?', { fileName: attachment.originalFileName })
    );
    if (!confirmed) {
      return;
    }

    attachmentDeleteMutation.mutate(String(attachment.id));
  };

  const openSelectedCreateForm = () => {
    if (!createRecordType) {
      return;
    }
    setCreateFlowStep('form');
  };

  const clearMaintenanceAiField = (field: string) => {
    setMaintenanceAiFilledFields((current) => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };

  const maintenanceDocumentTypeFor = (type: CreateRecordType | ''): string | null => {
    if (type === 'insurance') return 'VEHICLE_INSURANCE';
    if (type === 'inspection') return 'VEHICLE_INSPECTION';
    if (type === 'tax') return 'VEHICLE_TAX';
    return null;
  };

  const handleMaintenanceExtractFile = (file: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('maintenance.extract.fileTooLarge', 'Document size must be less than 10MB'));
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('maintenance.extract.invalidFileType', 'Upload a PDF, JPEG, PNG, or WEBP file'));
      return;
    }
    setMaintenanceExtractFile(file);
  };

  const handleExtractMaintenanceDoc = () => {
    const documentType = maintenanceDocumentTypeFor(createRecordType);
    if (!maintenanceExtractFile || !documentType) return;
    extractMaintenanceDocMutation.mutate({
      documentType,
      frontFile: maintenanceExtractFile,
    });
  };

  const handleCreateSave = () => {
    if (!selectedVehicleId) {
      toast.error(t('maintenance.errors.selectVehicleFirst', 'Select a vehicle first'));
      return;
    }

    const isEditing = editingRecordId !== null;

    switch (createRecordType) {
      case 'insurance': {
        const payload = {
          policyNumber: insuranceForm.policyNumber,
          providerCompany: insuranceForm.providerCompany,
          startDate: insuranceForm.startDate,
          expiryDate: insuranceForm.expiryDate,
          cost: toNullableNumber(insuranceForm.cost),
          notes: insuranceForm.notes,
        };
        isEditing ? updateInsuranceMutation.mutate(payload) : insuranceMutation.mutate(payload);
        break;
      }
      case 'inspection': {
        const payload = {
          inspectionDate: inspectionForm.inspectionDate,
          expiryDate: inspectionForm.expiryDate,
          inspectionCenter: inspectionForm.inspectionCenter,
          cost: toNullableNumber(inspectionForm.cost),
          notes: inspectionForm.notes,
        };
        isEditing ? updateInspectionMutation.mutate(payload) : inspectionMutation.mutate(payload);
        break;
      }
      case 'tax': {
        const payload = {
          taxType: taxForm.taxType,
          paymentDate: taxForm.paymentDate,
          expiryDate: taxForm.expiryDate,
          amount: toNullableNumber(taxForm.amount),
          notes: taxForm.notes,
        };
        isEditing ? updateTaxMutation.mutate(payload) : taxMutation.mutate(payload);
        break;
      }
      case 'schedule':
        scheduleMutation.mutate({
          serviceType: scheduleForm.serviceType,
          serviceTaskLabel: scheduleForm.serviceTaskLabel,
          description: scheduleForm.description,
          scheduleMode: scheduleForm.scheduleMode,
          dueRuleType: scheduleForm.dueRuleType,
          initialDueDate: scheduleForm.initialDueDate || undefined,
          initialDueMileageKm: toNullableNumber(scheduleForm.initialDueMileageKm),
          recurrenceKilometersKm: toNullableNumber(scheduleForm.recurrenceKilometersKm),
          recurrenceMonths: toNullableNumber(scheduleForm.recurrenceMonths),
          workshopProvider: scheduleForm.workshopProvider,
          notes: scheduleForm.notes,
        });
        break;
      case 'completion': {
        const payload = {
          serviceScheduleId: completionForm.serviceScheduleId ? Number(completionForm.serviceScheduleId) : undefined,
          serviceType: completionForm.serviceType,
          serviceTaskLabel: completionForm.serviceTaskLabel,
          completedOn: completionForm.completedOn,
          completedMileageKm: toNullableNumber(completionForm.completedMileageKm),
          cost: toNullableNumber(completionForm.cost),
          workshopProvider: completionForm.workshopProvider,
          notes: completionForm.notes,
        };
        isEditing ? updateCompletionMutation.mutate(payload) : completionMutation.mutate(payload);
        break;
      }
      case 'event': {
        const payload = {
          category: eventForm.category,
          title: eventForm.title,
          eventDate: eventForm.eventDate,
          mileageKm: toNullableNumber(eventForm.mileageKm),
          cost: toNullableNumber(eventForm.cost),
          providerName: eventForm.providerName,
          notes: eventForm.notes,
        };
        isEditing ? updateHistoryMutation.mutate(payload) : historyMutation.mutate(payload);
        break;
      }
      default:
        break;
    }
  };

  const isEditingRecord = editingRecordId !== null;

  const currentCreateLabel = (() => {
    switch (createRecordType) {
      case 'insurance':
        return isEditingRecord
          ? t('maintenance.createFlow.editInsurance', 'Edit insurance')
          : t('maintenance.createFlow.newInsurance', 'New insurance');
      case 'inspection':
        return isEditingRecord
          ? t('maintenance.createFlow.editInspection', 'Edit inspection / MOT')
          : t('maintenance.createFlow.newInspection', 'New inspection / MOT');
      case 'tax':
        return isEditingRecord
          ? t('maintenance.createFlow.editTax', 'Edit tax')
          : t('maintenance.createFlow.newTax', 'New tax');
      case 'schedule':
        return t('maintenance.createFlow.newSchedule', 'New service schedule');
      case 'completion':
        return isEditingRecord
          ? t('maintenance.createFlow.editCompletedService', 'Edit completed service')
          : t('maintenance.createFlow.recordCompletedService', 'Record completed service');
      case 'event':
        return isEditingRecord
          ? t('maintenance.createFlow.editEvent', 'Edit vehicle history event')
          : t('maintenance.createFlow.newEvent', 'New vehicle history event');
      default:
        return '';
    }
  })();

  const currentCreateHeading = (
    createFlowStep === 'idle'
      ? selectedVehicleName
      : currentCreateLabel
        ? t('maintenance.createFlow.headingFor', '{{label}} for {{vehicleName}}.', { label: currentCreateLabel, vehicleName: selectedVehicleName })
        : t('maintenance.createFlow.headingGeneric', 'Create maintenance record for {{vehicleName}}.', { vehicleName: selectedVehicleName })
  );

  const currentCreateLoading =
    insuranceMutation.isLoading ||
    updateInsuranceMutation.isLoading ||
    inspectionMutation.isLoading ||
    updateInspectionMutation.isLoading ||
    taxMutation.isLoading ||
    updateTaxMutation.isLoading ||
    scheduleMutation.isLoading ||
    completionMutation.isLoading ||
    updateCompletionMutation.isLoading ||
    historyMutation.isLoading ||
    updateHistoryMutation.isLoading;

  const renderMaintenanceExtractBox = () => {
    if (editingRecordId !== null) {
      return null;
    }
    return (
      <Grid item xs={12}>
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 0.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
              size="small"
              disabled={extractMaintenanceDocMutation.isLoading}
              sx={{ textTransform: 'none' }}
            >
              {maintenanceExtractFile ? maintenanceExtractFile.name : t('maintenance.extract.chooseFile', 'Choose Document')}
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) handleMaintenanceExtractFile(file);
                }}
              />
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={extractMaintenanceDocMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              disabled={!maintenanceExtractFile || extractMaintenanceDocMutation.isLoading}
              onClick={handleExtractMaintenanceDoc}
              sx={{ textTransform: 'none' }}
            >
              {extractMaintenanceDocMutation.isLoading
                ? t('maintenance.extract.extracting', 'Extracting...')
                : t('maintenance.extract.extractButton', 'Extract with AI')}
            </Button>
          </Stack>
        </Box>
      </Grid>
    );
  };

  const renderCreateFormContent = () => {
    switch (createRecordType) {
      case 'insurance':
        return (
          <Grid container spacing={1.5}>
            {renderMaintenanceExtractBox()}
            <Grid item xs={12} sm={5}>
              <TextField
                label={t('maintenance.fields.policyNumber', 'Policy number')}
                value={insuranceForm.policyNumber}
                onChange={(event) => {
                  setInsuranceForm({ ...insuranceForm, policyNumber: event.target.value });
                  clearMaintenanceAiField('policyNumber');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('policyNumber') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.providerCompany', 'Provider / company')}
                value={insuranceForm.providerCompany}
                onChange={(event) => {
                  setInsuranceForm({ ...insuranceForm, providerCompany: event.target.value });
                  clearMaintenanceAiField('providerCompany');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('providerCompany') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label={t('maintenance.fields.cost', 'Cost')}
                type="number"
                value={insuranceForm.cost}
                onChange={(event) => {
                  setInsuranceForm({ ...insuranceForm, cost: event.target.value });
                  clearMaintenanceAiField('cost');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('cost') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.startDate', 'Start date')}
                value={insuranceForm.startDate}
                onChange={(startDate) => setInsuranceForm({ ...insuranceForm, startDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.expiryDate', 'Expiry date')}
                value={insuranceForm.expiryDate}
                onChange={(expiryDate) => setInsuranceForm({ ...insuranceForm, expiryDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                multiline
                minRows={1}
                value={insuranceForm.notes}
                onChange={(event) => setInsuranceForm({ ...insuranceForm, notes: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        );
      case 'inspection':
        return (
          <Grid container spacing={1.5}>
            {renderMaintenanceExtractBox()}
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('maintenance.fields.inspectionCenter', 'Inspection center')}
                value={inspectionForm.inspectionCenter}
                onChange={(event) => {
                  setInspectionForm({ ...inspectionForm, inspectionCenter: event.target.value });
                  clearMaintenanceAiField('inspectionCenter');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('inspectionCenter') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.cost', 'Cost')}
                type="number"
                value={inspectionForm.cost}
                onChange={(event) => {
                  setInspectionForm({ ...inspectionForm, cost: event.target.value });
                  clearMaintenanceAiField('cost');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('cost') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.inspectionDate', 'Inspection date')}
                value={inspectionForm.inspectionDate}
                onChange={(inspectionDate) => setInspectionForm({ ...inspectionForm, inspectionDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.expiryDate', 'Expiry date')}
                value={inspectionForm.expiryDate}
                onChange={(expiryDate) => setInspectionForm({ ...inspectionForm, expiryDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                multiline
                minRows={1}
                value={inspectionForm.notes}
                onChange={(event) => setInspectionForm({ ...inspectionForm, notes: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        );
      case 'tax':
        return (
          <Grid container spacing={1.5}>
            {renderMaintenanceExtractBox()}
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('maintenance.fields.taxType', 'Tax type')}
                value={taxForm.taxType}
                onChange={(event) => {
                  setTaxForm({ ...taxForm, taxType: event.target.value });
                  clearMaintenanceAiField('taxType');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('taxType') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.amount', 'Amount')}
                type="number"
                value={taxForm.amount}
                onChange={(event) => {
                  setTaxForm({ ...taxForm, amount: event.target.value });
                  clearMaintenanceAiField('amount');
                }}
                size="small"
                fullWidth
                sx={maintenanceAiFilledFields.has('amount') ? aiFilledFieldSx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.paymentDate', 'Payment date')}
                value={taxForm.paymentDate}
                onChange={(paymentDate) => setTaxForm({ ...taxForm, paymentDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInputField
                label={t('maintenance.fields.expiryDate', 'Expiry date')}
                value={taxForm.expiryDate}
                onChange={(expiryDate) => setTaxForm({ ...taxForm, expiryDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                multiline
                minRows={1}
                value={taxForm.notes}
                onChange={(event) => setTaxForm({ ...taxForm, notes: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        );
      case 'schedule':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('maintenance.fields.serviceType', 'Service type')}
                value={scheduleForm.serviceType}
                onChange={(event) => setScheduleForm({ ...scheduleForm, serviceType: event.target.value as ServiceTaskType })}
                size="small"
                fullWidth
              >
                {serviceTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {enumLabel(t, 'serviceTaskTypes', option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.serviceLabel', 'Service label')}
                value={scheduleForm.serviceTaskLabel}
                onChange={(event) => setScheduleForm({ ...scheduleForm, serviceTaskLabel: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('maintenance.fields.scheduleMode', 'Schedule mode')}
                value={scheduleForm.scheduleMode}
                onChange={(event) => setScheduleForm({ ...scheduleForm, scheduleMode: event.target.value as ServiceScheduleMode })}
                size="small"
                fullWidth
              >
                <MenuItem value={ServiceScheduleMode.ONE_TIME}>{t('maintenance.options.oneTime', 'One-time')}</MenuItem>
                <MenuItem value={ServiceScheduleMode.RECURRING}>{t('maintenance.options.recurring', 'Recurring')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('maintenance.fields.dueRule', 'Due rule')}
                value={scheduleForm.dueRuleType}
                onChange={(event) => setScheduleForm({ ...scheduleForm, dueRuleType: event.target.value as ServiceDueRuleType })}
                size="small"
                fullWidth
              >
                <MenuItem value={ServiceDueRuleType.CALENDAR}>{t('maintenance.options.calendarOnly', 'Calendar only')}</MenuItem>
                <MenuItem value={ServiceDueRuleType.MILEAGE}>{t('maintenance.options.mileageOnly', 'Kilometers only')}</MenuItem>
                <MenuItem value={ServiceDueRuleType.MILEAGE_OR_CALENDAR}>{t('maintenance.options.mileageOrCalendar', 'Kilometers or calendar')}</MenuItem>
              </TextField>
            </Grid>
            {scheduleDialogNeedsDate && (
              <Grid item xs={12} sm={6}>
                <DateInputField
                  label={t('maintenance.fields.initialDueDate', 'Initial due date')}
                  value={scheduleForm.initialDueDate}
                  onChange={(initialDueDate) => setScheduleForm({ ...scheduleForm, initialDueDate })}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
            {scheduleDialogNeedsMileage && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('maintenance.fields.initialDueMileageKm', 'Initial due kilometers (km)')}
                  type="number"
                  value={scheduleForm.initialDueMileageKm}
                  onChange={(event) => setScheduleForm({ ...scheduleForm, initialDueMileageKm: event.target.value })}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
            {scheduleDialogIsRecurring && scheduleDialogNeedsMileage && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('maintenance.fields.repeatEveryKm', 'Repeat every X km')}
                  type="number"
                  value={scheduleForm.recurrenceKilometersKm}
                  onChange={(event) => setScheduleForm({ ...scheduleForm, recurrenceKilometersKm: event.target.value })}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
            {scheduleDialogIsRecurring && scheduleDialogNeedsDate && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('maintenance.fields.repeatEveryMonths', 'Repeat every X months')}
                  type="number"
                  value={scheduleForm.recurrenceMonths}
                  onChange={(event) => setScheduleForm({ ...scheduleForm, recurrenceMonths: event.target.value })}
                  size="small"
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.preferredWorkshop', 'Preferred workshop / provider')}
                value={scheduleForm.workshopProvider}
                onChange={(event) => setScheduleForm({ ...scheduleForm, workshopProvider: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.description', 'Description')}
                value={scheduleForm.description}
                onChange={(event) => setScheduleForm({ ...scheduleForm, description: event.target.value })}
                size="small"
                fullWidth
                multiline
                minRows={1}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                value={scheduleForm.notes}
                onChange={(event) => setScheduleForm({ ...scheduleForm, notes: event.target.value })}
                size="small"
                fullWidth
                multiline
                minRows={1}
              />
            </Grid>
          </Grid>
        );
      case 'completion':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('maintenance.fields.linkedSchedule', 'Linked schedule (optional)')}
                value={completionForm.serviceScheduleId}
                onChange={(event) => setCompletionForm({ ...completionForm, serviceScheduleId: event.target.value })}
                size="small"
                fullWidth
              >
                <MenuItem value="">{t('maintenance.options.adHocService', 'Ad hoc service')}</MenuItem>
                {(overviewQuery.data?.serviceSchedules ?? []).map((schedule) => (
                  <MenuItem key={schedule.id} value={String(schedule.id)}>
                    {schedule.serviceTaskLabel} ({statusLabel(t, schedule.status)})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t('maintenance.fields.serviceType', 'Service type')}
                value={completionForm.serviceType}
                onChange={(event) => setCompletionForm({ ...completionForm, serviceType: event.target.value as ServiceTaskType })}
                size="small"
                fullWidth
              >
                {serviceTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {enumLabel(t, 'serviceTaskTypes', option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.serviceLabel', 'Service label')}
                value={completionForm.serviceTaskLabel}
                onChange={(event) => setCompletionForm({ ...completionForm, serviceTaskLabel: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.workshopProvider', 'Workshop / provider')}
                value={completionForm.workshopProvider}
                onChange={(event) => setCompletionForm({ ...completionForm, workshopProvider: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DateInputField
                label={t('maintenance.fields.completedOn', 'Completed on')}
                value={completionForm.completedOn}
                onChange={(completedOn) => setCompletionForm({ ...completionForm, completedOn })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.completedMileageKm', 'Kilometers at completion (km)')}
                type="number"
                value={completionForm.completedMileageKm}
                onChange={(event) => setCompletionForm({ ...completionForm, completedMileageKm: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.cost', 'Cost')}
                type="number"
                value={completionForm.cost}
                onChange={(event) => setCompletionForm({ ...completionForm, cost: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                value={completionForm.notes}
                onChange={(event) => setCompletionForm({ ...completionForm, notes: event.target.value })}
                size="small"
                fullWidth
                multiline
                minRows={1}
              />
            </Grid>
          </Grid>
        );
      case 'event':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={t('maintenance.fields.category', 'Category')}
                value={eventForm.category}
                onChange={(event) => setEventForm({ ...eventForm, category: event.target.value as VehicleHistoryEventCategory })}
                size="small"
                fullWidth
              >
                {eventCategoryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {enumLabel(t, 'vehicleEventCategories', option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('maintenance.fields.title', 'Title')}
                value={eventForm.title}
                onChange={(event) => setEventForm({ ...eventForm, title: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DateInputField
                label={t('maintenance.fields.eventDate', 'Event date')}
                value={eventForm.eventDate}
                onChange={(eventDate) => setEventForm({ ...eventForm, eventDate })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.mileageKm', 'Kilometers (km)')}
                type="number"
                value={eventForm.mileageKm}
                onChange={(event) => setEventForm({ ...eventForm, mileageKm: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('maintenance.fields.cost', 'Cost')}
                type="number"
                value={eventForm.cost}
                onChange={(event) => setEventForm({ ...eventForm, cost: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.providerWorkshop', 'Provider / workshop')}
                value={eventForm.providerName}
                onChange={(event) => setEventForm({ ...eventForm, providerName: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('maintenance.fields.notes', 'Notes')}
                multiline
                minRows={1}
                value={eventForm.notes}
                onChange={(event) => setEventForm({ ...eventForm, notes: event.target.value })}
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (dashboardQuery.isError) {
    return (
      <Container maxWidth="xl" disableGutters sx={{ py: 3 }}>
        <Alert severity="error">{t('maintenance.errors.loadPageFailed', 'Failed to load the maintenance page.')}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
          width: '100%',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar
            sx={{
              pl: { xs: 2, sm: 3 },
              pr: { xs: 2, sm: 3 },
              minHeight: '48px !important',
              py: 0.5,
              gap: 1,
            }}
          >
            {createFlowStep === 'idle' ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startCreateFlow}
                disabled={!selectedVehicleId}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('common.new', 'New')}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={cancelCreateFlow}
                  disabled={currentCreateLoading}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                {createFlowStep === 'form' && (
                  <Button
                    variant="contained"
                    onClick={handleCreateSave}
                    disabled={currentCreateLoading}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.save', 'Save')}
                  </Button>
                )}
              </Stack>
            )}
            <Button
              variant="outlined"
              startIcon={<Refresh fontSize="small" />}
              onClick={refreshMaintenanceQueries}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={handleOpenAttachmentsDialog}
              disabled={!selectedVehicleId}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {t('maintenance.toolbar.uploadDocument', 'Upload document')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesome />}
              onClick={() => setBatchImportOpen(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {t('maintenance.batchImport.button', 'Batch Import')}
            </Button>
            {createFlowStep === 'idle' && detailTab === 1 && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => exportHistoryMutation.mutate()}
                disabled={!selectedVehicleId || overviewQuery.isLoading || exportHistoryMutation.isLoading}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('maintenance.toolbar.exportHistory', 'Export history')}
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('maintenance.toolbar.vehiclesCount', '{{count}} vehicles', { count: dashboardQuery.data?.metrics.vehiclesTracked ?? 0 })}
              {' • '}
              {t('maintenance.toolbar.withDueItems', '{{count}} with due items', { count: countDueVehicles(dashboardQuery.data) })}
            </Typography>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  gap: 1,
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
              >
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 0' },
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('maintenance.toolbar.vehiclesHeading', 'Maintenance Vehicles')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '0 0 auto' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    order: { xs: 3, md: 2 },
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={safePage === 0}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {filteredVehicles.length === 0
                      ? '0-0'
                      : `${safePage * rowsPerPage + 1}-${Math.min((safePage + 1) * rowsPerPage, filteredVehicles.length)}`}
                    {` ${t('maintenance.toolbar.of', 'of')} `}
                    {filteredVehicles.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                    disabled={safePage >= totalPages - 1}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 0' },
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'space-between', md: 'flex-end' },
                    gap: 1,
                    order: { xs: 2, md: 3 },
                  }}
                >
                  <TextField
                    select
                    size="small"
                    label={t('maintenance.toolbar.rows', 'Rows')}
                    value={rowsPerPage}
                    onChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    sx={{ width: 96 }}
                  >
                    {[5, 10, 25, 50].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    startIcon={<FilterListOff fontSize="small" />}
                    onClick={clearFilters}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    {t('maintenance.toolbar.clearFilters', 'Clear filters')}
                  </Button>
                </Box>
              </Toolbar>

              <TableContainer>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableFilterCell
                        placeholder={t('maintenance.table.vehicle', 'Vehicle')}
                        value={columnFilters.vehicle}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, vehicle: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('vehicle')}
                        sortDirection={sortConfig?.key === 'vehicle' ? sortConfig.direction : null}
                        width={220}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.table.plate', 'Plate')}
                        value={columnFilters.plate}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, plate: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('plate')}
                        sortDirection={sortConfig?.key === 'plate' ? sortConfig.direction : null}
                        width={130}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.table.mileage', 'Kilometers')}
                        value={columnFilters.mileage}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, mileage: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('mileage')}
                        sortDirection={sortConfig?.key === 'mileage' ? sortConfig.direction : null}
                        width={120}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.attachmentOwner.insurance', 'Insurance')}
                        value={columnFilters.insurance}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, insurance: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('insurance')}
                        sortDirection={sortConfig?.key === 'insurance' ? sortConfig.direction : null}
                        width={130}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.table.inspection', 'Inspection')}
                        value={columnFilters.inspection}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, inspection: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('inspection')}
                        sortDirection={sortConfig?.key === 'inspection' ? sortConfig.direction : null}
                        width={130}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.attachmentOwner.tax', 'Tax')}
                        value={columnFilters.tax}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, tax: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('tax')}
                        sortDirection={sortConfig?.key === 'tax' ? sortConfig.direction : null}
                        width={120}
                      />
                      <TableFilterCell
                        placeholder={t('maintenance.table.serviceDue', 'Service due')}
                        value={columnFilters.service}
                        onChange={(value) => {
                          setColumnFilters((current) => ({ ...current, service: value }));
                          setPage(0);
                        }}
                        onSort={() => handleSort('service')}
                        sortDirection={sortConfig?.key === 'service' ? sortConfig.direction : null}
                        width={120}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedVehicles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          {t('maintenance.emptyState.noVehiclesForFilters', 'No vehicles found for the current filters.')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedVehicles.map((item) => {
                        const parsed = parseVehicleLabel(item.vehicleLabel);
                        return (
                          <TableRow
                            key={item.vehicleId}
                            hover
                            selected={String(item.vehicleId) === selectedVehicleId}
                            onClick={() => setSelectedVehicleId(String(item.vehicleId))}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                              '&.Mui-selected': { bgcolor: 'action.selected' },
                            }}
                          >
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {parsed.name}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              {parsed.plate || '—'}
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              {formatKm(item.currentMileageKm)}
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              <Chip size="small" label={friendlyStatus(t, item.insurance.status)} color={statusColor(item.insurance.status)} />
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              <Chip size="small" label={friendlyStatus(t, item.inspection.status)} color={statusColor(item.inspection.status)} />
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              <Chip size="small" label={friendlyStatus(t, item.tax.status)} color={statusColor(item.tax.status)} />
                            </TableCell>
                            <TableCell sx={{ py: 0.85, px: 1.5 }}>
                              <Chip
                                size="small"
                                label={`${item.dueServiceSchedules.length}`}
                                color={item.dueServiceSchedules.length > 0 ? 'warning' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {selectedVehicleId && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 0, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="h6">
                        {currentCreateHeading}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={overviewQuery.data?.vehicle.status ? statusLabel(t, overviewQuery.data.vehicle.status) : t('common.unknown', 'Unknown')}
                        size="small"
                      />
                      <Chip
                        label={formatKm(overviewQuery.data?.vehicle.currentMileageKm ?? selectedDueItem?.currentMileageKm)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </Box>

                {createFlowStep !== 'idle' ? (
                  <Box
                    sx={{
                      px: { xs: 2, sm: 3 },
                      pb: { xs: 2, sm: 3 },
                      pt: { xs: 1.25, sm: 1.75 },
                    }}
                  >
                    {createFlowStep === 'selectType' ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <TextField
                          select
                          size="small"
                          label={t('maintenance.createFlow.typeLabel', 'Type')}
                          value={createRecordType}
                          onChange={(event) => setCreateRecordType(event.target.value as CreateRecordType | '')}
                          sx={{ width: { xs: '100%', sm: 280 } }}
                        >
                          <MenuItem value="" disabled>
                            {t('maintenance.createFlow.selectType', 'Select type')}
                          </MenuItem>
                          {createRecordOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(`maintenance.createOptions.${option.value}`, option.label)}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={openSelectedCreateForm}
                          disabled={!createRecordType}
                          sx={{ textTransform: 'none', minWidth: 112 }}
                        >
                          {t('common.create', 'Create')}
                        </Button>
                      </Stack>
                    ) : renderCreateFormContent()}
                  </Box>
                ) : overviewQuery.isLoading ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : overviewQuery.isError ? (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="error">{t('maintenance.errors.loadDetailsFailed', 'Failed to load the selected vehicle maintenance details.')}</Alert>
                  </Box>
                ) : overviewQuery.data ? (
                  <Box sx={{ p: { xs: 2, sm: 3 } }}>
                    <Tabs
                      value={detailTab}
                      onChange={(_, value) => setDetailTab(value)}
                      sx={{ mb: 1, minHeight: 40 }}
                    >
                      <Tab label={t('common.details')} id="maintenance-tab-0" aria-controls="maintenance-tabpanel-0" sx={{ minHeight: 40 }} />
                      <Tab label={t('maintenance.tabs.serviceHistory', 'Service History')} id="maintenance-tab-1" aria-controls="maintenance-tabpanel-1" sx={{ minHeight: 40 }} />
                      <Tab label={t('maintenance.tabs.mileageHistory', 'Kilometers History')} id="maintenance-tab-2" aria-controls="maintenance-tabpanel-2" sx={{ minHeight: 40 }} />
                    </Tabs>

                    <TabPanel value={detailTab} index={0}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} lg={3}>
                        <SummaryTile
                          title={t('maintenance.attachmentOwner.insurance', 'Insurance')}
                          value={overviewQuery.data.insurance.label || t('maintenance.summary.noInsuranceRecord', 'No insurance record')}
                          helper={overviewQuery.data.insurance.expiryDate ? t('maintenance.summary.expires', 'Expires {{date}}', { date: formatDate(overviewQuery.data.insurance.expiryDate) }) : undefined}
                          status={overviewQuery.data.insurance.status}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <SummaryTile
                          title={t('maintenance.attachmentOwner.inspection', 'Inspection / MOT')}
                          value={overviewQuery.data.inspection.label || t('maintenance.summary.noInspectionRecord', 'No inspection record')}
                          helper={overviewQuery.data.inspection.expiryDate ? t('maintenance.summary.expires', 'Expires {{date}}', { date: formatDate(overviewQuery.data.inspection.expiryDate) }) : undefined}
                          status={overviewQuery.data.inspection.status}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <SummaryTile
                          title={t('maintenance.attachmentOwner.tax', 'Tax')}
                          value={overviewQuery.data.tax.label || t('maintenance.summary.noTaxRecord', 'No tax record')}
                          helper={overviewQuery.data.tax.expiryDate ? t('maintenance.summary.expires', 'Expires {{date}}', { date: formatDate(overviewQuery.data.tax.expiryDate) }) : undefined}
                          status={overviewQuery.data.tax.status}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3}>
                        <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('maintenance.summary.serviceSchedules', 'Service schedules')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {t('maintenance.summary.activeCount', '{{count}} active', { count: activeScheduleCount })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t('maintenance.summary.overdueCount', '{{count}} overdue', { count: overdueScheduleCount })}
                          </Typography>
                        </Paper>
                      </Grid>
	                    </Grid>

	                    <Divider sx={{ my: 2 }} />

	                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
	                      {t('maintenance.mileage.sectionTitle', 'Kilometers details')}
	                    </Typography>
	                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
	                      <Stack
	                        direction={{ xs: 'column', lg: 'row' }}
	                        spacing={2}
	                        alignItems={{ xs: 'stretch', lg: 'flex-start' }}
	                        justifyContent="space-between"
	                      >
	                        <Grid container spacing={2} sx={{ flex: 1 }}>
	                          <Grid item xs={12} sm={6} md={4}>
	                            <Typography variant="caption" color="text.secondary">
	                              {t('maintenance.mileage.currentMileage', 'Current kilometers')}
	                            </Typography>
	                            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>
	                              {formatKm(overviewQuery.data.mileage.currentMileageKm ?? overviewQuery.data.vehicle.currentMileageKm)}
	                            </Typography>
	                          </Grid>
	                          <Grid item xs={12} sm={6} md={4}>
	                            <Typography variant="caption" color="text.secondary">
	                              {t('maintenance.mileage.lastUpdated', 'Last updated')}
	                            </Typography>
	                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
	                              {formatDateTime(overviewQuery.data.mileage.lastUpdatedAt)}
	                            </Typography>
	                          </Grid>
	                          <Grid item xs={12} sm={6} md={4}>
	                            <Typography variant="caption" color="text.secondary">
	                              {t('maintenance.mileage.lastUpdatedBy', 'Last updated by')}
	                            </Typography>
	                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
	                              {overviewQuery.data.mileage.lastUpdatedBy || '-'}
	                            </Typography>
	                          </Grid>
	                          <Grid item xs={12} sm={6} md={4}>
	                            <Typography variant="caption" color="text.secondary">
	                              {t('maintenance.mileage.lastSource', 'Last source')}
	                            </Typography>
	                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
	                              {formatMileageSource(t, overviewQuery.data.mileage.lastSource)}
	                            </Typography>
	                            {overviewQuery.data.mileage.lastReference ? (
	                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
	                                {t('maintenance.mileage.reference', 'Ref: {{reference}}', { reference: overviewQuery.data.mileage.lastReference })}
	                              </Typography>
	                            ) : null}
	                          </Grid>
	                          <Grid item xs={12} sm={6} md={4}>
	                            <Typography variant="caption" color="text.secondary">
	                              {t('maintenance.mileage.totalRecordedUpdates', 'Total recorded updates')}
	                            </Typography>
	                            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
	                              {overviewQuery.data.mileage.totalRecordedUpdates}
	                            </Typography>
	                          </Grid>
	                        </Grid>
	                        {!isMileageEditorOpen ? (
	                          <Button
	                            variant="contained"
	                            onClick={handleOpenMileageEditor}
	                            size="small"
	                            sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', lg: 'flex-start' } }}
	                          >
	                            {t('maintenance.mileage.updateMileage', 'Update kilometers')}
	                          </Button>
	                        ) : null}
	                      </Stack>
	                      {isMileageEditorOpen ? (
	                        <Box
	                          sx={{
	                            mt: 2,
	                            pt: 2,
	                            borderTop: '1px solid',
	                            borderColor: 'divider',
	                          }}
	                        >
	                          <Grid container spacing={2}>
	                            <Grid item xs={12} sm={4}>
	                              <TextField
	                                fullWidth
	                                type="number"
	                                label={t('maintenance.mileage.currentMileage', 'Current kilometers')}
	                                value={mileageUpdateForm.currentMileageKm}
	                                onChange={(event) => setMileageUpdateForm((current) => ({
	                                  ...current,
	                                  currentMileageKm: event.target.value,
	                                }))}
	                                disabled={mileageUpdateMutation.isLoading}
	                                inputProps={{ min: 0, step: 1 }}
	                              />
	                            </Grid>
	                            <Grid item xs={12} sm={8}>
	                              <TextField
	                                fullWidth
	                                label={t('maintenance.mileage.notesOptional', 'Notes (optional)')}
	                                value={mileageUpdateForm.notes}
	                                onChange={(event) => setMileageUpdateForm((current) => ({
	                                  ...current,
	                                  notes: event.target.value,
	                                }))}
	                                disabled={mileageUpdateMutation.isLoading}
	                              />
	                            </Grid>
	                            <Grid item xs={12}>
	                              <Stack direction="row" spacing={1}>
	                                <Button
	                                  variant="contained"
	                                  onClick={handleSaveMileageUpdate}
	                                  disabled={mileageUpdateMutation.isLoading}
	                                  sx={{ textTransform: 'none' }}
	                                >
	                                  {mileageUpdateMutation.isLoading ? t('maintenance.mileage.saving', 'Saving...') : t('common.save', 'Save')}
	                                </Button>
	                                <Button
	                                  variant="outlined"
	                                  onClick={handleCancelMileageEditor}
	                                  disabled={mileageUpdateMutation.isLoading}
	                                  sx={{ textTransform: 'none' }}
	                                >
	                                  {t('common.cancel', 'Cancel')}
	                                </Button>
	                              </Stack>
	                        </Grid>
	                          </Grid>
	                        </Box>
	                      ) : null}
	                    </Paper>

	                    <Divider sx={{ my: 2 }} />

	                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
	                      {t('maintenance.summary.serviceSchedules', 'Service schedules')}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('maintenance.table.task', 'Task')}</TableCell>
                            <TableCell>{t('maintenance.table.rule', 'Rule')}</TableCell>
                            <TableCell>{t('maintenance.table.nextDue', 'Next due')}</TableCell>
                            <TableCell>{t('maintenance.table.lastCompletion', 'Last completion')}</TableCell>
                            <TableCell>{t('maintenance.table.status', 'Status')}</TableCell>
                            <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewQuery.data.serviceSchedules.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 2.5 }}>
                                {t('maintenance.emptyState.noServiceSchedules', 'No service schedules recorded yet.')}
                              </TableCell>
                            </TableRow>
                          ) : (
                            overviewQuery.data.serviceSchedules.map((schedule) => (
                              <TableRow key={schedule.id}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {schedule.serviceTaskLabel}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {enumLabel(t, 'serviceTaskTypes', schedule.serviceType)}
                                  </Typography>
                                  {schedule.sourceTemplateId && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        label={t('maintenance.schedule.fleetDefault', { templateName: schedule.sourceTemplateName })}
                                      />
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {enumLabel(t, 'maintenance.options', schedule.scheduleMode)} / {enumLabel(t, 'dueRuleTypes', schedule.dueRuleType)}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatScheduleDueTarget(schedule)}</TableCell>
                                <TableCell>{formatScheduleLastCompletion(schedule)}</TableCell>
                                <TableCell>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip size="small" label={statusLabel(t, schedule.status)} />
                                    <Chip size="small" label={friendlyStatus(t, schedule.dueStatus)} color={statusColor(schedule.dueStatus)} />
                                  </Stack>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    {schedule.status === ServiceScheduleStatus.ACTIVE && (
                                      <>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => scheduleStatusMutation.mutate({
                                            scheduleId: schedule.id,
                                            status: ServiceScheduleStatus.PAUSED,
                                          })}
                                        >
                                          {t('maintenance.schedule.pause', 'Pause')}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => scheduleStatusMutation.mutate({
                                            scheduleId: schedule.id,
                                            status: ServiceScheduleStatus.CANCELLED,
                                          })}
                                        >
                                          {t('common.cancel', 'Cancel')}
                                        </Button>
                                      </>
                                    )}
                                    {schedule.status === ServiceScheduleStatus.PAUSED && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => scheduleStatusMutation.mutate({
                                          scheduleId: schedule.id,
                                          status: ServiceScheduleStatus.ACTIVE,
                                        })}
                                      >
                                        {t('maintenance.schedule.resume', 'Resume')}
                                      </Button>
                                    )}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    </TabPanel>

		                    <TabPanel value={detailTab} index={1}>
		                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
		                        {t('maintenance.tabs.serviceHistory', 'Service History')}
		                      </Typography>
		                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
		                        <Table size="small">
		                          <TableHead>
		                            <TableRow>
		                              <TableCell sx={{ width: 140 }}>{t('maintenance.table.date', 'Date')}</TableCell>
		                              <TableCell sx={{ width: 220 }}>{t('maintenance.table.task', 'Task')}</TableCell>
		                              <TableCell sx={{ width: 160 }}>{t('maintenance.table.type', 'Type')}</TableCell>
		                              <TableCell sx={{ width: 130 }}>{t('maintenance.table.mileage', 'Kilometers')}</TableCell>
		                              <TableCell sx={{ width: 180 }}>{t('maintenance.table.provider', 'Provider')}</TableCell>
		                              <TableCell sx={{ width: 130 }}>{t('maintenance.fields.cost', 'Cost')}</TableCell>
		                              <TableCell>{t('maintenance.fields.notes', 'Notes')}</TableCell>
		                            </TableRow>
		                          </TableHead>
		                          <TableBody>
		                            {serviceHistoryRows.length === 0 ? (
		                              <TableRow>
		                                <TableCell colSpan={7} align="center" sx={{ py: 2.5 }}>
		                                  {t('maintenance.emptyState.noServiceHistory', 'No service history recorded yet.')}
		                                </TableCell>
		                              </TableRow>
		                            ) : (
		                              serviceHistoryRows.map((entry) => (
		                                <TableRow key={entry.id}>
		                                  <TableCell>{formatDate(entry.completedOn)}</TableCell>
		                                  <TableCell>
		                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
		                                      {entry.serviceTaskLabel}
		                                    </Typography>
		                                  </TableCell>
		                                  <TableCell>{enumLabel(t, 'serviceTaskTypes', entry.serviceType)}</TableCell>
		                                  <TableCell>{formatKm(entry.completedMileageKm)}</TableCell>
		                                  <TableCell>{entry.workshopProvider || '-'}</TableCell>
		                                  <TableCell>{formatOptionalCurrency(entry.cost)}</TableCell>
		                                  <TableCell>{entry.notes || '-'}</TableCell>
		                                </TableRow>
		                              ))
		                            )}
		                          </TableBody>
		                        </Table>
		                      </TableContainer>

		                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
		                      {t('maintenance.table.maintenanceHistory', 'Maintenance history')}
		                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 120 }}>{t('maintenance.table.km', 'KM')}</TableCell>
                            <TableCell sx={{ width: 120 }}>{t('maintenance.table.date', 'Date')}</TableCell>
                            <TableCell sx={{ width: 150 }}>{t('maintenance.table.type', 'Type')}</TableCell>
                            <TableCell sx={{ width: 220 }}>{t('maintenance.fields.title', 'Title')}</TableCell>
                            <TableCell sx={{ width: 220 }}>{t('common.details', 'Details')}</TableCell>
                            <TableCell sx={{ width: 120 }}>{t('maintenance.table.amount', 'Amount')}</TableCell>
                            <TableCell>{t('maintenance.fields.notes', 'Notes')}</TableCell>
                            <TableCell align="right" sx={{ width: 90 }}>{t('common.actions', 'Actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {historyRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 2.5 }}>
                                {t('maintenance.emptyState.noMaintenanceHistory', 'No maintenance history recorded yet.')}
                              </TableCell>
                            </TableRow>
                          ) : (
                            historyRows.map((entry) => {
                              const editableType = editableRecordTypeForSourceType(entry.sourceType);
                              return (
                              <TableRow key={`${entry.sourceType}-${entry.sourceId}`}>
                                <TableCell>{formatKm(entry.mileageKm)}</TableCell>
                                <TableCell>{formatOptionalDate(entry.occurredOn)}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={historyTypeLabel(t, entry)} variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {entry.title}
                                  </Typography>
                                </TableCell>
                                <TableCell>{entry.subtitle || '-'}</TableCell>
                                <TableCell>{formatOptionalCurrency(entry.amount)}</TableCell>
                                <TableCell>{entry.notes || '-'}</TableCell>
                                <TableCell align="right">
                                  {editableType && (
                                    <IconButton
                                      size="small"
                                      onClick={() => startEditFlow(editableType, entry.sourceId)}
                                      disabled={createFlowStep !== 'idle'}
                                      title={t('common.edit', 'Edit')}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
	                    </TabPanel>

	                    <TabPanel value={detailTab} index={2}>
	                      <Box
	                        sx={{
	                          display: 'flex',
	                          alignItems: 'center',
	                          justifyContent: 'space-between',
	                          gap: 1,
	                          mb: 1,
	                          flexWrap: 'wrap',
	                        }}
	                      >
	                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
	                          {t('maintenance.tabs.mileageHistory', 'Kilometers History')}
	                        </Typography>
	                        <Button
	                          variant="outlined"
	                          startIcon={<FilterListOff fontSize="small" />}
	                          onClick={clearMileageHistoryFilters}
	                          size="small"
	                          sx={{ textTransform: 'none' }}
	                        >
	                          {t('maintenance.toolbar.clearFilters', 'Clear filters')}
	                        </Button>
	                      </Box>
	                      <TableContainer component={Paper} variant="outlined">
	                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
	                          <TableHead>
	                            <TableRow>
	                              <TableCell sx={{ width: 150 }}>{t('maintenance.table.date', 'Date')}</TableCell>
	                              <TableCell sx={{ width: 110 }}>{t('maintenance.table.previousKm', 'Previous KM')}</TableCell>
	                              <TableCell sx={{ width: 110 }}>{t('maintenance.table.newKm', 'New KM')}</TableCell>
	                              <TableCell sx={{ width: 110 }}>{t('maintenance.table.difference', 'Difference')}</TableCell>
	                              <TableCell sx={{ width: 140 }}>{t('maintenance.table.source', 'Source')}</TableCell>
	                              <TableCell sx={{ width: 140 }}>{t('maintenance.table.reference', 'Reference')}</TableCell>
	                              <TableCell sx={{ width: 160 }}>{t('maintenance.table.updatedBy', 'Updated by')}</TableCell>
	                              <TableCell>{t('maintenance.fields.notes', 'Notes')}</TableCell>
	                            </TableRow>
	                            <TableRow>
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.date', 'Date')}
	                                value={mileageHistoryFilters.date}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, date: value }))}
	                                onSort={() => handleMileageHistorySort('date')}
	                                sortDirection={mileageHistorySortConfig.key === 'date' ? mileageHistorySortConfig.direction : null}
	                                width={150}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.previous', 'Previous')}
	                                value={mileageHistoryFilters.previousKm}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, previousKm: value }))}
	                                onSort={() => handleMileageHistorySort('previousKm')}
	                                sortDirection={mileageHistorySortConfig.key === 'previousKm' ? mileageHistorySortConfig.direction : null}
	                                width={110}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.new', 'New')}
	                                value={mileageHistoryFilters.newKm}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, newKm: value }))}
	                                onSort={() => handleMileageHistorySort('newKm')}
	                                sortDirection={mileageHistorySortConfig.key === 'newKm' ? mileageHistorySortConfig.direction : null}
	                                width={110}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.diff', 'Diff')}
	                                value={mileageHistoryFilters.difference}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, difference: value }))}
	                                onSort={() => handleMileageHistorySort('difference')}
	                                sortDirection={mileageHistorySortConfig.key === 'difference' ? mileageHistorySortConfig.direction : null}
	                                width={110}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.source', 'Source')}
	                                value={mileageHistoryFilters.source}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, source: value }))}
	                                onSort={() => handleMileageHistorySort('source')}
	                                sortDirection={mileageHistorySortConfig.key === 'source' ? mileageHistorySortConfig.direction : null}
	                                width={140}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.reference', 'Reference')}
	                                value={mileageHistoryFilters.reference}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, reference: value }))}
	                                onSort={() => handleMileageHistorySort('reference')}
	                                sortDirection={mileageHistorySortConfig.key === 'reference' ? mileageHistorySortConfig.direction : null}
	                                width={140}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.table.updatedBy', 'Updated by')}
	                                value={mileageHistoryFilters.updatedBy}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, updatedBy: value }))}
	                                onSort={() => handleMileageHistorySort('updatedBy')}
	                                sortDirection={mileageHistorySortConfig.key === 'updatedBy' ? mileageHistorySortConfig.direction : null}
	                                width={160}
	                              />
	                              <TableFilterCell
	                                placeholder={t('maintenance.fields.notes', 'Notes')}
	                                value={mileageHistoryFilters.notes}
	                                onChange={(value) => setMileageHistoryFilters((current) => ({ ...current, notes: value }))}
	                                onSort={() => handleMileageHistorySort('notes')}
	                                sortDirection={mileageHistorySortConfig.key === 'notes' ? mileageHistorySortConfig.direction : null}
	                              />
	                            </TableRow>
	                          </TableHead>
	                          <TableBody>
	                            {mileageHistoryRows.length === 0 ? (
	                              <TableRow>
	                                <TableCell colSpan={8} align="center" sx={{ py: 2.5 }}>
	                                  {t('maintenance.emptyState.noMileageHistory', 'No kilometers history recorded yet.')}
	                                </TableCell>
	                              </TableRow>
	                            ) : (
	                              mileageHistoryRows.map((entry) => (
	                                <TableRow key={entry.id}>
	                                  <TableCell>{formatDateTime(entry.updatedAt)}</TableCell>
	                                  <TableCell>{formatKm(entry.previousMileageKm)}</TableCell>
	                                  <TableCell>{formatKm(entry.newMileageKm)}</TableCell>
	                                  <TableCell>{formatMileageDifference(entry.differenceKm)}</TableCell>
	                                  <TableCell>
	                                    <Chip size="small" label={formatMileageSource(t, entry.source)} variant="outlined" />
	                                  </TableCell>
	                                  <TableCell>{entry.reference || '-'}</TableCell>
	                                  <TableCell>{entry.updatedByDisplayName || '-'}</TableCell>
	                                  <TableCell>{entry.notes || '-'}</TableCell>
	                                </TableRow>
	                              ))
	                            )}
	                          </TableBody>
	                        </Table>
	                      </TableContainer>
	                    </TabPanel>
	                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="info">{t('maintenance.emptyState.selectVehicle', 'Select a vehicle to open its maintenance details.')}</Alert>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog
        open={openAttachmentsDialog}
        onClose={handleCloseAttachmentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('maintenance.dialog.title', 'Maintenance documents - {{vehicleName}}', { vehicleName: selectedVehicleName })}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="maintenance-attachment-owner-type-label">{t('maintenance.dialog.recordType', 'Record type')}</InputLabel>
                <Select
                  labelId="maintenance-attachment-owner-type-label"
                  value={selectedAttachmentOwnerType}
                  label={t('maintenance.dialog.recordType', 'Record type')}
                  onChange={(event) => setSelectedAttachmentOwnerType(event.target.value as AttachmentOwnerType)}
                >
                  {attachmentOwnerOptions.map((option) => (
                    <MenuItem key={option.ownerType} value={option.ownerType}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 280, flex: 1 }}>
                <InputLabel id="maintenance-attachment-record-label">{t('maintenance.dialog.maintenanceRecord', 'Maintenance record')}</InputLabel>
                <Select
                  labelId="maintenance-attachment-record-label"
                  value={selectedAttachmentOwnerRecordId}
                  label={t('maintenance.dialog.maintenanceRecord', 'Maintenance record')}
                  onChange={(event) => setSelectedAttachmentOwnerRecordId(event.target.value)}
                  disabled={!selectedAttachmentOwnerOption || selectedAttachmentOwnerOption.records.length === 0}
                >
                  {(selectedAttachmentOwnerOption?.records ?? []).map((record) => (
                    <MenuItem key={record.id} value={record.id}>
                      {record.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                label={t('maintenance.fields.notes', 'Notes')}
                value={attachmentNotes}
                onChange={(event) => setAttachmentNotes(event.target.value)}
                sx={{ minWidth: 260, flex: 1 }}
              />

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                size="small"
                disabled={attachmentUploadMutation.isLoading}
                sx={{ textTransform: 'none' }}
              >
                {t('maintenance.dialog.chooseDocument', 'Choose document')}
                <input
                  hidden
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(event) => {
                    setSelectedAttachmentFile(event.target.files?.[0] || null);
                    event.target.value = '';
                  }}
                />
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleUploadAttachment}
                disabled={
                  !selectedAttachmentFile ||
                  !selectedAttachmentOwnerType ||
                  !selectedAttachmentOwnerRecordId ||
                  attachmentUploadMutation.isLoading
                }
                sx={{ textTransform: 'none' }}
              >
                {attachmentUploadMutation.isLoading ? t('maintenance.dialog.uploading', 'Uploading...') : t('maintenance.dialog.upload', 'Upload')}
              </Button>
            </Box>

            {attachmentOwnerOptions.length === 0 ? (
              <Alert severity="info">
                {t('maintenance.dialog.noOwnerRecordsHint', 'Create an insurance, inspection, tax, service history, or vehicle event record first, then attach files to it here.')}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {selectedAttachmentFile?.name || t('maintenance.dialog.noDocumentSelected', 'No document selected')}
              </Typography>
            )}

            {attachmentUploadMutation.isLoading && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  {t('maintenance.dialog.uploadingAttachment', 'Uploading attachment...')}
                </Typography>
                <LinearProgress
                  variant={attachmentUploadProgress > 0 ? 'determinate' : 'indeterminate'}
                  value={attachmentUploadProgress || undefined}
                />
              </Box>
            )}

            {overviewQuery.isLoading ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : (overviewQuery.data?.attachments.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('maintenance.dialog.noAttachmentsYet', 'No maintenance documents uploaded for this vehicle yet.')}
              </Typography>
            ) : (
              <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {overviewQuery.data?.attachments.map((attachment) => (
                  <ListItem
                    key={attachment.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      alignItems: 'flex-start',
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={() => handlePreviewAttachment(attachment)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDownloadAttachment(attachment)}>
                          <UploadFile fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAttachment(attachment)}
                          disabled={attachmentDeleteMutation.isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 13 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {attachment.originalFileName}
                          </Typography>
                          <Chip
                            label={enumLabel(t, 'maintenance.attachmentOwner', attachment.ownerType)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ pr: 13, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {[
                              attachmentOwnerLabelByKey.get(`${attachment.ownerType}:${attachment.ownerRecordId}`),
                              formatAttachmentFileSize(attachment.fileSizeBytes),
                              formatAttachmentDateTime(t, attachment.createdAt),
                            ].filter(Boolean).join(' | ')}
                          </Typography>
                          {attachment.notes ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {attachment.notes}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                      sx={{ my: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachmentsDialog}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <BatchMaintenanceImportDialog
        open={batchImportOpen}
        onClose={() => setBatchImportOpen(false)}
        onImported={refreshMaintenanceQueries}
      />
    </Box>
  );
};
